// Installs `window.__HARNESS__` (src/app/harness-contract.ts) for a page the visual harness
// (tools/visual-harness) captures. The page supplies three callbacks; the bridge owns the
// readiness promise, shot settling, frame timing and heap sampling.
//
//   const bridge = installHarness({ applyShot, isSettled, counters });
//   renderer.setAnimationLoop(() => { renderer.render(scene, camera); bridge.frame(); });
//   await loadEverything();
//   bridge.markReady();                        // or bridge.fail(err) → harness reports it
//
//   harness                     bridge                          page
//   ───────                     ──────                          ────
//   await ready        ◄──────  markReady() + 1 frame
//   setShot(shot)      ──────►  applyShot(shot)       ──────►   start camera move, load data
//                               poll isSettled() each frame
//                      ◄──────  N settled frames in a row
//   measure(ms)        ──────►  frame() timestamps (or rAF) ──► counters()
//                      ◄──────  HarnessStats

import type { HarnessApi, HarnessStats, Shot } from './harness-contract.ts';

export type Backend = HarnessStats['backend'];

/** What the page's renderer reports for its latest frame. */
export interface RenderCounters {
  drawCalls: number;
  triangles: number;
  backend: Backend;
  /** Markers drawn without a source id (truth rule 1; must be 0). */
  unsourcedMarkers: number;
}

export interface HarnessHooks {
  /** Start moving toward the shot: camera, and date/leader/lens/mode/panel when given. */
  applyShot(shot: Shot): void | Promise<void>;
  /** True when the camera has arrived and nothing is pending (chunks, textures, data). */
  isSettled(): boolean;
  counters(): RenderCounters;
}

export interface HarnessOptions {
  /** Consecutive settled frames required before setShot resolves. */
  settleFrames?: number;
  /** setShot rejects when the scene has not settled within this time. */
  settleTimeoutMs?: number;
}

export interface HarnessBridge {
  api: HarnessApi;
  /** Data loaded and the first frame rendered; resolves `ready` after one more frame. */
  markReady(): void;
  /** Rejects `ready` so the harness fails with this reason instead of timing out. */
  fail(reason: unknown): void;
  /** Call once per rendered frame; measure() times frames from these calls. */
  frame(): void;
}

const SETTLE_FRAMES = 3;
const SETTLE_TIMEOUT_MS = 60_000;
const BYTES_PER_MB = 1024 * 1024;
const P50 = 0.5;
const P99 = 0.99;
const MS_PER_S = 1000;

// A page timed by rAF alone reports display cadence; it needs at least two stamps for one interval.
const MIN_STAMPS = 2;

// measure(ms) runs past `ms` until this many frames were stamped, so a slow frame (SwiftShader
// takes over a second at close zoom) still yields real numbers; MEASURE_CAP_MS bounds the wait.
const MIN_FRAMES = 4;
const MEASURE_CAP_MS = 30_000;

/** Backend the harness asked for (`?backend=webgl2`), so pages can force WebGL 2. */
export function requestedBackend(): Backend | null {
  const value = new URLSearchParams(location.search).get('backend');
  if (value === 'webgpu' || value === 'webgl2') {
    return value;
  }
  return null;
}

export function installHarness(hooks: HarnessHooks, options: HarnessOptions = {}): HarnessBridge {
  const settleFrames = options.settleFrames ?? SETTLE_FRAMES;
  const settleTimeoutMs = options.settleTimeoutMs ?? SETTLE_TIMEOUT_MS;

  let resolveReady: () => void = () => {};
  let rejectReady: (reason: unknown) => void = () => {};
  const ready = new Promise<void>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });

  // The harness awaits `ready`; a page that fails before anyone listens must not log an
  // "unhandled rejection" console error on top of the real one.
  ready.catch(() => {});

  let loadMs = 0;
  let recording = false;
  let stamps: number[] = [];

  function markReady(): void {
    if (loadMs > 0) {
      return;
    }
    loadMs = performance.now();
    requestAnimationFrame(() => resolveReady());
  }

  function fail(reason: unknown): void {
    rejectReady(reason instanceof Error ? reason : new Error(String(reason)));
  }

  function frame(): void {
    if (recording) {
      stamps.push(performance.now());
    }
  }

  // Resolve after `settleFrames` consecutive settled frames; reject on timeout.
  function settle(name: string): Promise<void> {
    const started = performance.now();
    let streak = 0;

    return new Promise((resolve, reject) => {
      const poll = () => {
        streak = hooks.isSettled() ? streak + 1 : 0;
        if (streak >= settleFrames) {
          resolve();
          return;
        }
        if (performance.now() - started > settleTimeoutMs) {
          reject(new Error(`shot "${name}" did not settle within ${settleTimeoutMs} ms`));
          return;
        }
        requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    });
  }

  async function setShot(shot: Shot): Promise<void> {
    await hooks.applyShot(shot);
    await settle(shot.name);
  }

  // Record frame() stamps for `ms` (and at least MIN_FRAMES frames); pages that never call
  // frame() are timed by rAF instead.
  function measure(ms: number): Promise<HarnessStats> {
    stamps = [];
    recording = true;
    const rafStamps: number[] = [];
    const started = performance.now();

    return new Promise((resolve) => {
      const tick = (now: number) => {
        rafStamps.push(now);
        const elapsed = now - started;
        const timed = stamps.length >= MIN_STAMPS ? stamps : rafStamps;
        const enough = elapsed >= ms && timed.length >= MIN_FRAMES;
        if (!enough && elapsed < ms + MEASURE_CAP_MS) {
          requestAnimationFrame(tick);
          return;
        }
        recording = false;
        resolve(stats(timed));
      };
      requestAnimationFrame(tick);
    });
  }

  function stats(timed: number[]): HarnessStats {
    const frames = intervals(timed);
    const span = timed.length >= MIN_STAMPS ? timed[timed.length - 1] - timed[0] : 0;
    const counters = hooks.counters();

    return {
      fps: span > 0 ? (frames.length / span) * MS_PER_S : 0,
      frameMsP50: percentile(frames, P50),
      frameMsP99: percentile(frames, P99),
      drawCalls: counters.drawCalls,
      triangles: counters.triangles,
      backend: counters.backend,
      heapMb: heapMb(),
      loadMs,
      unsourcedMarkers: counters.unsourcedMarkers,
    };
  }

  const api: HarnessApi = { ready, setShot, measure };
  window.__HARNESS__ = api;
  return { api, markReady, fail, frame };
}

/** Differences between consecutive timestamps, e.g. [0, 16, 40] → [16, 24]. */
function intervals(stamps: number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < stamps.length; i++) {
    out.push(stamps[i] - stamps[i - 1]);
  }
  return out;
}

function percentile(values: number[], p: number): number {
  if (values.length === 0) {
    return 0;
  }
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
}

// Chrome-only `performance.memory`; other browsers report 0.
function heapMb(): number {
  const memory = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
  if (!memory) {
    return 0;
  }
  return Math.round((memory.usedJSHeapSize / BYTES_PER_MB) * 10) / 10;
}
